import { createContext, useState, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';

export const tokens = (mode) => ({
	...(mode === 'dark'
		? {
				grey: {
					100: '#f1f5f9',
					200: '#e2e8f0',
					300: '#cbd5e1',
					400: '#94a3b8',
					500: '#64748b',
					600: '#475569',
					700: '#334155',
					800: '#1e293b',
					900: '#0f172a',
				},
				primary: {
					100: '#e8eef7',
					200: '#c7d6ea',
					300: '#8fa8cc',
					400: '#152238',
					500: '#0a0e17',
					600: '#080b12',
					700: '#060810',
					800: '#04060c',
					900: '#020308',
				},
				greenAccent: {
					100: '#dbf5ee',
					200: '#b7ebde',
					300: '#94e2cd',
					400: '#70d8bd',
					500: '#4cceac',
					600: '#3da58a',
					700: '#2e7c67',
					800: '#1e5245',
					900: '#0f2922',
				},
				redAccent: {
					100: '#f8dcdb',
					200: '#f1b9b7',
					300: '#e99592',
					400: '#e2726e',
					500: '#db4f4a',
					600: '#af3f3b',
					700: '#832f2c',
					800: '#58201e',
					900: '#2c100f',
				},
				blueAccent: {
					100: '#dbeafe',
					200: '#bfdbfe',
					300: '#93c5fd',
					400: '#60a5fa',
					500: '#3b82f6',
					600: '#2563eb',
					700: '#1d4ed8',
					800: '#1e40af',
					900: '#1e3a8a',
				},
		  }
		: {
				grey: {
					100: '#0f172a',
					200: '#1e293b',
					300: '#334155',
					400: '#475569',
					500: '#64748b',
					600: '#94a3b8',
					700: '#cbd5e1',
					800: '#e2e8f0',
					900: '#f1f5f9',
				},
				primary: {
					100: '#020617',
					200: '#0f172a',
					300: '#1e293b',
					400: '#1a4d8c',
					500: '#f0f4f8',
					600: '#e8edf3',
					700: '#dce3ec',
					800: '#cfd8e3',
					900: '#ffffff',
				},
				greenAccent: {
					100: '#0f2922',
					200: '#1e5245',
					300: '#2e7c67',
					400: '#3da58a',
					500: '#4cceac',
					600: '#70d8bd',
					700: '#94e2cd',
					800: '#b7ebde',
					900: '#dbf5ee',
				},
				redAccent: {
					100: '#2c100f',
					200: '#58201e',
					300: '#832f2c',
					400: '#af3f3b',
					500: '#db4f4a',
					600: '#e2726e',
					700: '#e99592',
					800: '#f1b9b7',
					900: '#f8dcdb',
				},
				blueAccent: {
					100: '#eff6ff',
					200: '#dbeafe',
					300: '#bfdbfe',
					400: '#60a5fa',
					500: '#2563eb',
					600: '#1d4ed8',
					700: '#1e40af',
					800: '#1e3a8a',
					900: '#172554',
				},
		  }),
});

export const themeSettings = (mode) => {
	const colors = tokens(mode);
	return {
		palette: {
			mode: mode,
			...(mode === 'dark'
				? {
						primary: {
							main: colors.blueAccent[300],
						},
						secondary: {
							main: colors.blueAccent[500],
						},
						neutral: {
							dark: colors.grey[700],
							main: colors.grey[500],
							light: colors.grey[100],
						},
						background: {
							default: colors.primary[500],
							paper: colors.primary[400],
						},
						redAccent: {
							main: colors.redAccent[500],
						},
						green: {
							main: colors.greenAccent[500],
						},
				  }
				: {
						primary: {
							main: colors.blueAccent[600],
						},
						secondary: {
							main: colors.blueAccent[500],
						},
						neutral: {
							dark: colors.grey[700],
							main: colors.grey[500],
							light: colors.grey[100],
						},
						background: {
							default: colors.primary[500],
							paper: colors.primary[900],
						},
						redAccent: {
							main: colors.redAccent[500],
						},
						green: {
							main: colors.greenAccent[400],
						},
				  }),
		},
		typography: {
			fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
			fontSize: 12,
			h1: {
				fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
				fontSize: 40,
			},
			h2: {
				fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
				fontSize: 32,
			},
			h3: {
				fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
				fontSize: 24,
			},
			h4: {
				fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
				fontSize: 20,
			},
			h5: {
				fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
				fontSize: 16,
			},
			h6: {
				fontFamily: ['Source Sans 3', 'Source Sans Pro', 'sans-serif'].join(','),
				fontSize: 14,
			},
		},
	};
};

export const ColorModeContext = createContext({
	toggleColorMode: () => {},
});

export const useMode = () => {
	const [mode, setMode] = useState('light');

	const colorMode = useMemo(
		() => ({
			toggleColorMode: () =>
				setMode((prev) => (prev === 'light' ? 'dark' : 'light')),
		}),
		[]
	);

	const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
	return [theme, colorMode];
};
